import React, { useEffect, useRef, useState } from "react";
import { loadConnectAndInitialize, type StripeConnectInstance } from "@stripe/connect-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { wooRequest } from "../lib/woocommerce";
import { Loader2, AlertTriangle } from "lucide-react";

interface StripeConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Active store config (native) — used to reach payments/stripe-connect. */
  config: any;
  /** Called after the embedded component exits — refresh payment status. */
  onExit?: () => void;
  /** 'onboarding' (default) mounts the KYC flow; 'management' mounts account management. */
  component?: 'account-onboarding' | 'account-management';
}

// Stripe Connect embedded onboarding — the merchant completes Stripe's own
// KYC/IBAN flow inside this modal. The backend creates an Express account +
// AccountSession and returns a short-lived client_secret.
export function StripeConnectDialog({ open, onOpenChange, config, onExit, component = 'account-onboarding' }: StripeConnectDialogProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStatus('loading');
    setError(null);
    let cancelled = false;
    const el = mountRef.current;

    let publishableKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY || '';
    const fetchClientSecret = async () => {
      const res = await wooRequest(config, 'payments/stripe-connect', {
        method: 'POST',
        body: { action: 'onboard' },
      });
      const secret = res?.client_secret || res?.data?.client_secret;
      if (res?.publishable_key) publishableKey = res.publishable_key;
      if (!secret) throw new Error(res?.error || 'Stripe Connect não respondeu.');
      return secret;
    };

    // Watchdog — if the SDK or network stalls, surface an error instead of
    // spinning forever (e.g. connect-js blocked by an extension/adblock).
    const watchdog = setTimeout(() => {
      if (!cancelled) {
        setStatus(s => {
          if (s === 'loading') {
            setError('A Stripe demorou demasiado a responder. Se tem um bloqueador de anúncios/extensões, desative-o e tente de novo.');
            return 'error';
          }
          return s;
        });
      }
    }, 20000);

    (async () => {
      try {
        await fetchClientSecret();
        if (cancelled || !el) return;
        const connectInstance: StripeConnectInstance = loadConnectAndInitialize({
          publishableKey,
          fetchClientSecret,
          appearance: { overlays: 'dialog' },
        });
        const comp: any = connectInstance.create(component);
        comp.setOnExit?.(() => { onOpenChange(false); onExit?.(); });
        // Surface embedded-component failures (expired session, script
        // blocked) instead of leaving Stripe's own spinner hanging.
        comp.addEventListener?.('loaderror', (ev: any) => {
          if (!cancelled) {
            setStatus('error');
            setError(ev?.error?.message || 'O componente Stripe falhou a carregar.');
          }
        });
        el.innerHTML = '';
        el.appendChild(comp as HTMLElement);
        setStatus('ready');
      } catch (e: any) {
        if (!cancelled) { setStatus('error'); setError(e?.message || 'Erro ao iniciar o onboarding Stripe.'); }
      }
    })();

    return () => { cancelled = true; clearTimeout(watchdog); if (el) el.innerHTML = ''; };
  }, [open, component]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] h-[85vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-2">
          <DialogTitle>Stripe — receber pagamentos</DialogTitle>
          <DialogDescription>
            Conclua o onboarding seguro da Stripe: dados da empresa, titular e IBAN. O dinheiro das vendas cai diretamente na sua conta.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
          {status === 'loading' && (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-brand" />
            </div>
          )}
          {status === 'error' && (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
              <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
            </div>
          )}
          <div ref={mountRef} className="min-h-[500px] h-full" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
